import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EntityState, Entity, EntityCreate, EntityUpdate } from '../../types';
import { entityApi } from '../../services/entityApi';

// Helper function to normalize entity data
const normalizeEntity = (entity: any): Entity => {
  
  
  const normalized = {
    ...entity,
    // Ensure we have both id and entity_id for backward compatibility
    id: entity.entity_id || entity.id,
    entity_id: entity.entity_id || entity.id,
    // Map backend fields to frontend fields
    name: entity.name ?? 'Unnamed Entity',
    description: entity.description ?? 'No description',
    data: entity.data ?? entity.content ?? entity.text ?? entity.body ?? 'No content',
    processed_data: entity.processed_data,
    processing_status: entity.processing_status,
    last_processed: entity.last_processed,
    // Backward compatibility fields
    status: entity.status ?? entity.processing_status ?? 'active',
    is_processed: (entity.processing_status === 'completed') || (entity.is_processed ?? false),
    file_count: entity.file_count ?? 0,
    url_count: entity.url_count ?? 0,
    created_at: entity.created_at ?? new Date().toISOString(),
    updated_at: entity.updated_at ?? new Date().toISOString(),
    user_id: entity.user_id ?? 'unknown',
  };
  
  
  return normalized;
};

const initialState: EntityState = {
  entities: [],
  isLoading: false,
  error: null,
};

export const fetchEntities = createAsyncThunk(
  'entities/fetchEntities',
  async (_, { rejectWithValue }) => {
    try {
      
      const response = await entityApi.getEntities();
      
      return response;
    } catch (error: any) {
      
      return rejectWithValue(error.message || 'Failed to fetch entities');
    }
  }
);

export const createEntity = createAsyncThunk(
  'entities/createEntity',
  async (entityData: EntityCreate, { rejectWithValue }) => {
    try {
      const response = await entityApi.createEntity(entityData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create entity');
    }
  }
);

export const updateEntity = createAsyncThunk(
  'entities/updateEntity',
  async ({ entityId, entityData }: { entityId: string; entityData: EntityUpdate }, { rejectWithValue }) => {
    try {
      const response = await entityApi.updateEntity(entityId, entityData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update entity');
    }
  }
);

export const processEntity = createAsyncThunk(
  'entities/processEntity',
  async (entityId: string, { rejectWithValue }) => {
    try {
      const response = await entityApi.processEntity(entityId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to process entity');
    }
  }
);

export const deleteEntity = createAsyncThunk(
  'entities/deleteEntity',
  async (entityId: string, { rejectWithValue }) => {
    try {
      await entityApi.deleteEntity(entityId);
      return entityId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete entity');
    }
  }
);

const entitySlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch entities
      .addCase(fetchEntities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntities.fulfilled, (state, action: PayloadAction<Entity[]>) => {
        state.isLoading = false;
        state.entities = action.payload.map(normalizeEntity);
      })
      .addCase(fetchEntities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create entity
      .addCase(createEntity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createEntity.fulfilled, (state, action: PayloadAction<Entity>) => {
        state.isLoading = false;
        state.entities.unshift(normalizeEntity(action.payload));
      })
      .addCase(createEntity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update entity
      .addCase(updateEntity.fulfilled, (state, action: PayloadAction<Entity>) => {
        const normalizedEntity = normalizeEntity(action.payload);
        const index = state.entities.findIndex(e => 
          e.entity_id === normalizedEntity.entity_id || 
          e.id === normalizedEntity.id ||
          e.entity_id === normalizedEntity.id ||
          e.id === normalizedEntity.entity_id
        );
        if (index !== -1) {
          state.entities[index] = normalizedEntity;
        }
      })
      // Process entity
      .addCase(processEntity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(processEntity.fulfilled, (state, action: PayloadAction<Entity>) => {
        state.isLoading = false;
        const normalizedEntity = normalizeEntity(action.payload);
        const index = state.entities.findIndex(e => 
          e.entity_id === normalizedEntity.entity_id || 
          e.id === normalizedEntity.id ||
          e.entity_id === normalizedEntity.id ||
          e.id === normalizedEntity.entity_id
        );
        if (index !== -1) {
          state.entities[index] = normalizedEntity;
        }
      })
      .addCase(processEntity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete entity
      .addCase(deleteEntity.fulfilled, (state, action: PayloadAction<string>) => {
        state.entities = state.entities.filter(e => 
          e.entity_id !== action.payload && e.id !== action.payload
        );
      });
  },
});

export const { clearError } = entitySlice.actions;
export default entitySlice.reducer;
